import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { ExpenseItemForm } from './ExpenseItemForm'
import type { ExpenseItemData, ExpenseItemInput } from '../schema/expensesSchema'
import type { ExpensesCategory } from '../types/expenses'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { formatCurrency } from '@/utils/currencyUtils'
import { usePermissions } from '@/utils/permissions'

interface ExpenseItemsTableProps {
  items: Array<ExpenseItemInput | ExpenseItemData>
  categories: Array<ExpensesCategory>
  internal?: boolean
  disabled?: boolean
  allowDelete?: boolean
  onChange: (items: Array<ExpenseItemInput | ExpenseItemData>) => void
}

export function ExpenseItemsTable({
  items,
  categories,
  disabled,
  allowDelete,
  onChange,
}: ExpenseItemsTableProps) {
  const permissions = usePermissions()
  const canDelete = allowDelete ?? permissions.canDeleteExpenseItem()

  const [editIndex, setEditIndex] = useState<number | null>(null)

  const handleAddItem = (item: ExpenseItemInput | ExpenseItemData) => {
    onChange([...items, item])
  }

  const handleEditItem = (
    index: number,
    item: ExpenseItemInput | ExpenseItemData,
  ) => {
    const newItems = [...items]
    newItems[index] = item
    onChange(newItems)
  }

  const handleDeleteItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index)
    onChange(newItems)
  }

  const openAddForm = () => {
    setEditIndex(-1) // -1 indicates "add mode"
  }

  const openEditForm = (index: number) => {
    setEditIndex(index)
  }

  const closeForm = () => {
    setEditIndex(null)
  }

  const handleSubmit = (item: ExpenseItemInput | ExpenseItemData) => {
    if (editIndex !== null && editIndex >= 0) {
      handleEditItem(editIndex, item)
    } else {
      handleAddItem(item)
    }
  }

  const getCategoryName = (categoryId: string) => {
    return categories.find((c) => c._id === categoryId)?.name || 'Unknown'
  }

  const truncateFilename = (name: string, prefixLen = 8) => {
    const dotIndex = name.lastIndexOf('.')
    const ext = dotIndex !== -1 ? name.slice(dotIndex) : ''
    const base = dotIndex !== -1 ? name.slice(0, dotIndex) : name
    if (base.length <= prefixLen) return name
    return base.slice(0, prefixLen) + '....' + ext
  }

  const getReceiptDisplay = (item: ExpenseItemInput | ExpenseItemData) => {
    if ('file' in item && item.file instanceof File) {
      return truncateFilename(item.file.name)
    }
    if ('receiptUrl' in item && item.receiptUrl) {
      const fileName = item.receiptUrl.split('/').pop() || 'receipt'
      return truncateFilename(fileName)
    }
    return 'No receipt'
  }

  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Expense Items</h3>
          {items.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {items.length} item{items.length !== 1 ? 's' : ''} · Total:{' '}
              {formatCurrency(totalAmount)}
            </p>
          )}
        </div>
        {items.length > 0 && (
          <Button
            type="button"
            onClick={openAddForm}
            disabled={disabled}
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-muted-foreground mb-4">
              No expense items added yet.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={openAddForm}
              disabled={disabled}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Receipt</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {item.itemDescription}
                    </TableCell>
                    <TableCell>{getCategoryName(item.categoryId)}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {getReceiptDisplay(item)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditForm(index)}
                          disabled={disabled}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {canDelete && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteItem(index)}
                            disabled={disabled}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {items.map((item, index) => (
              <Card
                className="relative cursor-pointer hover:bg-accent transition-colors"
                key={index}
                onClick={() => openEditForm(index)}
              >
                {canDelete && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteItem(index)
                    }}
                    disabled={disabled}
                    className="absolute top-2 right-2 h-6 w-6 p-0"
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                )}
                <CardContent className="px-3 py-0 pr-12">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 space-y-1 min-w-0">
                      <h4 className="font-semibold text-sm">
                        {item.itemDescription}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {getCategoryName(item.categoryId)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end space-y-1 shrink-0">
                      <span className="text-base font-bold">
                        {formatCurrency(item.amount)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {getReceiptDisplay(item)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      <ExpenseItemForm
        expenseItem={
          editIndex !== null && editIndex >= 0 ? items[editIndex] : undefined
        }
        categories={categories}
        open={editIndex !== null}
        onClose={closeForm}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
